/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Import component UI form cho Sổ Theo Dõi Thuế GTGT (S61-DN)
import ValueAddedTaxFilingInfo from "./info";

import { ValueAddedTaxFilingService } from "services/value-added-tax-filing";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

// 1. Initial values khớp DTO CreateValueAddedTaxRefundDto
const initialValues = {
  company: "",
  companyAddress: "",
  year: today.getFullYear().toString(),
  openingPaidVatAmount: "",
  openingPayableVatAmount: "",
  items: [],
  totalArisingPaidVatAmount: "",
  totalArisingPayableVatAmount: "",
  closingPaidVatAmount: "",
  closingPayableVatAmount: "",
  totalPages: "",
  openingDate: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
  preparerName: "",
  chiefAccountantName: "",
  legalRepresentativeName: "",
  status: "OPEN",
};

function ValueAddedTaxFilingDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [data, setData] = useState({});

  const [formInitials, setFormInitials] = useState(initialValues);

  // 2. Load data từ API và map vào form
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await ValueAddedTaxFilingService.getById(id);
        const apiData = response.data ? response.data : response;

        // Map items đúng schema VAT Filing
        const mappedItems = (apiData.items || []).map((item) => ({
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate
            ? new Date(item.documentDate).toLocaleDateString("vi-VN")
            : "",
          description: item.description || "",
          paidVatAmount: item.paidVatAmount || "",
          payableVatAmount: item.payableVatAmount || "",
        }));

        const loadedData = {
          ...initialValues,
          company: apiData.company?.name || apiData.company || "",
          companyAddress: apiData.companyAddress || "",
          year: apiData.year?.toString() || initialValues.year,
          openingPaidVatAmount: apiData.openingPaidVatAmount || "",
          openingPayableVatAmount: apiData.openingPayableVatAmount || "",
          items: mappedItems.length > 0 ? mappedItems : [],
          totalArisingPaidVatAmount: apiData.totalArisingPaidVatAmount || "",
          totalArisingPayableVatAmount: apiData.totalArisingPayableVatAmount || "",
          closingPaidVatAmount: apiData.closingPaidVatAmount || "",
          closingPayableVatAmount: apiData.closingPayableVatAmount || "",
          totalPages: apiData.totalPages || "",
          openingDate: apiData.openingDate
            ? new Date(apiData.openingDate).toLocaleDateString("vi-VN")
            : "",
          signingDay: apiData.signingDay?.toString() || initialValues.signingDay,
          signingMonth: apiData.signingMonth?.toString() || initialValues.signingMonth,
          signingYear: apiData.signingYear?.toString() || initialValues.signingYear,
          preparerName: apiData.preparerName || "",
          chiefAccountantName: apiData.chiefAccountantName || "",
          legalRepresentativeName: apiData.legalRepresentativeName || "",
          status: apiData.status || "OPEN",
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi getById Sổ Theo Dõi Thuế GTGT:", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const onChangeValue = useCallback((type, value) => {
    setData((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleBack = () => navigation(-1);

  // 3. Submit payload 100% khớp CreateValueAddedTaxRefundDto
  const submitForm = async (values) => {
    try {
      const num = (val) => (val && !isNaN(val) ? Number(val) : 0);

      const getValue = (field) => (data[field] !== undefined ? data[field] : values[field]);
      const itemsFlat = getValue("items") || [];

      // Build items theo VAT Filing schema
      const buildItems = (itemsList) =>
        itemsList.map((item) => ({
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate ? new Date(item.documentDate) : null,
          description: item.description || "",
          paidVatAmount: num(item.paidVatAmount),
          payableVatAmount: num(item.payableVatAmount),
        }));

      // Build signingDate ISO nếu có
      const signD = getValue("signingDay");
      const signM = getValue("signingMonth");
      const signY = getValue("signingYear");
      let signingDate = null;
      if (signY && signM && signD) {
        signingDate = new Date(
          `${signY}-${String(signM).padStart(2, "0")}-${String(signD).padStart(2, "0")}`
        ).toISOString();
      }

      // Payload đúng theo VAT Filing DTO
      const payload = {
        companyAddress: getValue("companyAddress") || "",
        year: num(getValue("year")),
        openingPaidVatAmount: num(getValue("openingPaidVatAmount")),
        openingPayableVatAmount: num(getValue("openingPayableVatAmount")),
        items: buildItems(itemsFlat),
        totalPages: num(getValue("totalPages")),
        openingDate: getValue("openingDate") ? new Date(getValue("openingDate")) : null,
        signingDate: signingDate,
        preparerName: getValue("preparerName") || "",
        chiefAccountantName: getValue("chiefAccountantName") || "",
        legalRepresentativeName: getValue("legalRepresentativeName") || "",
        status: getValue("status") || "OPEN",
      };

      console.log("✅ ValueAddedTaxFiling Payload:", JSON.stringify(payload, null, 2));

      if (!id) {
        await ValueAddedTaxFilingService.create(payload);
        toast.success("Tạo Sổ Theo Dõi Thuế GTGT thành công!");
      } else {
        await ValueAddedTaxFilingService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
    } catch (error) {
      console.error("Lỗi lưu Sổ Theo Dõi Thuế GTGT:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = (values) => {
    const finalData = { ...values, ...data };
    const htmlContent = getPrintTemplate({
      headerInfo: finalData,
      rows: finalData.items || [],
      totals: {
        totalArisingPaidVatAmount: finalData.totalArisingPaidVatAmount,
        totalArisingPayableVatAmount: finalData.totalArisingPayableVatAmount,
        closingPaidVatAmount: finalData.closingPaidVatAmount,
        closingPayableVatAmount: finalData.closingPayableVatAmount,
      },
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSubmit = async (values, actions) => {
    try {
      await submitForm(values);
    } catch (error) {
      // Error đã handle trong submitForm
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <ValueAddedTaxFilingInfo
                          formData={{
                            values,
                            onChangeValue,
                          }}
                        />

                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Sổ Theo Dõi Thuế GTGT (S61-DN)
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu" : "Tạo"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default ValueAddedTaxFilingDetail;
